import type { MongoAbility } from '@casl/ability';

/**
 * Error thrown when a user tries to access a route they don't have permission for.
 * This is caught by the `GenericErrorBoundary` to display a 403 Forbidden page.
 */
export class RouteAuthorizationError extends Error {
	constructor(message?: string) {
		super(message ?? "You don't have permission to view this page.");
		this.name = 'RouteAuthorizationError';
	}
}

export interface RequireAbilityArgs {
	ability: MongoAbility;
	action: string;
	subject: string;
}

/**
 * Checks if the current user has the required ability to access a route.
 * Redirects to login if the user is not authenticated.
 * Throws RouteAuthorizationError if the user lacks permission.
 *
 * The ability and currentUser are created during app initialization in entry-client.tsx and
 * are available in the router context.
 *
 * @throws {RouteAuthorizationError} When the user lacks the required permission
 */
export function requireAbility(args: RequireAbilityArgs): void {
	// If user is authenticated but lacks permission, throw 403
	if (!args.ability.can(args.action, args.subject)) {
		throw new RouteAuthorizationError();
	}
}
